import React, { useState, FC, PropsWithChildren, useContext } from 'react'
import { Link } from 'react-router5'
import { styled } from '@mui/material/styles'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Drawer from '@mui/material/Drawer'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import MenuIcon from '@mui/icons-material/Menu'
import { useTheme } from '@mui/material/styles'

import Backdrop from '@mui/material/Backdrop'
import CircularProgress from '@mui/material/CircularProgress'
import Snackbar from '@mui/material/Snackbar'
import Alert from '@mui/material/Alert'

import useRouter from '../hooks/useRouter'
import { LoadingContext } from '../contexts/loading'
import { SnackbarContext } from '../contexts/snackbar'
import { AccountContext } from '../contexts/account'

const drawerWidth = 320

const AppBarSpacer = styled('div')(({ theme }) => ({
  ...theme.mixins.toolbar
}))

const Layout: FC<PropsWithChildren> = ({
  children,
}) => {

  const { navigate } = useRouter()
  const [open, setOpen] = useState(false)
  const theme = useTheme()

  const { loading } = useContext(LoadingContext)
  const { snackbar, setSnackbar } = useContext(SnackbarContext)
  const account = useContext(AccountContext)

  const handleDrawerOpen = () => setOpen(true)
  const handleDrawerClose = () => setOpen(false)

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(undefined)
  };

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100%'
    }}>
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer - 1 }}>
        <Toolbar
          sx={{
            bgcolor: 'background.paper',
            color: 'text.primary',
            '& a': {
              textDecoration: 'none',
              color: 'inherit',
            },
          }}
        >
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{ mr: 2, ...(open && { display: 'none' }) }}
          >
            <MenuIcon />
          </IconButton>

          <Link routeName="admin-videos">
            <img
              src="/img/temp-logo.png"
              alt="Logo"
              style={{
                width: 160,
                marginRight: theme.spacing(2),
              }}
            />
          </Link>

          <Box sx={{ flexGrow: 1 }} />
          {account.user && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {account.user.email}
              </Typography>
              <Button size="small" onClick={account.logout}>
                Logout
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      <Box component="main" sx={{
        flexGrow: 1,
        transition: (theme) => theme.transitions.create(['margin'], {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
        ...(open && {
          marginLeft: `${drawerWidth}px`,
        }),
      }}>
        <AppBarSpacer />
        <Container maxWidth="xl" sx={{ mt: 2 }}>
          {account.initialized && children}
        </Container>
      </Box>


      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            pt: 0,
          },
        }}
        variant="persistent"
        anchor="left"
        open={open}
      >
        <Button onClick={handleDrawerClose}>
          <ChevronLeftIcon /> Close
        </Button>
        <Divider />
        <List>
          <ListItem
            component={ButtonBase}
            onClick={() => {
              navigate('admin-home')
              handleDrawerClose()
            }}
          >
            <ListItemText primary="Home" />
          </ListItem>
          <ListItem
            component={ButtonBase}
            onClick={() => {
              navigate('admin-videos')
              handleDrawerClose()
            }}
          >
            <ListItemText primary="Videos" />
          </ListItem>
        </List>
      </Drawer>

      {/* Global Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Global Snackbar */}
      <Snackbar
        open={!!snackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {snackbar && (
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{
              width: '100%',
              bgcolor: snackbar.color,
              color: 'white',
              border: `1px solid #333333`,
              '& .MuiAlert-icon': {
                color: 'white'
              }
            }}
          >
            {snackbar.message}
          </Alert>
        )}
      </Snackbar>

    </Box>
  )
}

export default Layout
